import { ChangeEventHandler, PropsWithChildren, useState } from "react";
import { FormProvider, Path, useForm, useFormContext } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";

import { renewSubscription } from "~/api/stripe/renew";
import { updateUser } from "~/api/users/updateUser";
import MainButton from "~/components/common/mainButton";
import { useAuth } from "~/contexts/providers/auth";
import { useLanguage } from "~/contexts/providers/language";
import englishTranslations from "~/localization/en";
import frenchTranslations from "~/localization/fr";
import { SubscriptionFrequencyType } from "~/types/subscription/subscription.types";
import { getPaymentText } from "~/utils/functions/general.functions";

import PaymentModal from "../paymentModal";

type SubscriptionFields = {
  automaticRenewal?: boolean;
  subscriptionFrequency?: SubscriptionFrequencyType;
};

import styles from "./index.module.scss";

type InputProps = {
  name: Path<SubscriptionFields>;
  label: string;
  uncheckedText: string;
  checkedText: string;
  defaultChecked: boolean;
  readonly?: boolean;
  onchange?: ChangeEventHandler<HTMLInputElement>;
};

const Input = ({
  label,
  name,
  uncheckedText,
  checkedText,
  defaultChecked,
  readonly = false,
  onchange,
  children,
}: PropsWithChildren<InputProps>) => {
  const { register } = useFormContext<SubscriptionFields>();

  return (
    <div className={styles.inputContainer}>
      <label>{label}</label>
      {children}
      <div>
        <span>{uncheckedText}</span>
        <div className={styles.input}>
          <input
            {...register(name)}
            type="checkbox"
            defaultChecked={defaultChecked}
            readOnly={readonly}
            disabled={readonly}
            onChange={onchange}
          />
          <span />
        </div>

        <span>{checkedText}</span>
      </div>
    </div>
  );
};

const SubscriptionForm = () => {
  const [modalOpen, setModalOpen] = useState(false);

  const methods = useForm<SubscriptionFields>();
  const { user } = useAuth();
  const { language } = useLanguage();
  const accountTexts =
    language === "En"
      ? englishTranslations.accountPage
      : frenchTranslations.accountPage;
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation(renewSubscription);
  const { mutate: changeSubscriptionFrequency } = useMutation(updateUser, {
    onSuccess: () => {
      queryClient.invalidateQueries("user");
    },
    // onError: () => {
    //   setError("Une erreur est survenue, veuillez réessayer.");
    // },
  });

  if (!user) return null;

  const { freeAccessUntil, subscription } = user;
  const monthlySubscription =
    user?.subscriptionFrequency === SubscriptionFrequencyType.MONTHLY;

  const handleSubscriptionFrequencyChange: ChangeEventHandler<
    HTMLInputElement
  > = (e) => {
    changeSubscriptionFrequency({
      subscriptionFrequency: e.target.checked
        ? SubscriptionFrequencyType.MONTHLY
        : SubscriptionFrequencyType.YEARLY,
    });
  };

  const handleSubmit = async (data: SubscriptionFields) => {
    if (!subscription) {
      setModalOpen(true);
      return;
    }
    if (data.automaticRenewal !== undefined)
      await mutateAsync(data.automaticRenewal);
  };

  return (
    <>
      <FormProvider {...methods}>
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            methods.handleSubmit(handleSubmit)();
          }}
        >
          <h5>{accountTexts.mySubscription}</h5>
          {!subscription && !!freeAccessUntil && (
            <>
              Accès gratuit jusqu'au{" "}
              {new Date(freeAccessUntil).toLocaleDateString("fr")}
            </>
          )}
          {/* {!!subscription && (
            <Input
              name="automaticRenewal"
              uncheckedText="Pas de renouvellement"
              checkedText="Renouvellement automatique"
              label="Renouvellement le"
              defaultChecked={subscription.renew}
            >
              {subscription.renewalDate ? (
                <p className={styles.expirationDate}>
                  {new Date(subscription.renewalDate).toLocaleDateString("fr")}
                </p>
              ) : null}
            </Input>
          )}
          {!!subscription ? (
            <p className={styles.expirationDate}>
              {subscription.subscriptionFrequency === "MONTHLY"
                ? "Paiment mensuel"
                : "Paiment annuel"}
            </p>
          ) : (
            <Input
              name="subscriptionFrequency"
              uncheckedText="Paiment annuel"
              checkedText="Paiment mensuel"
              label="Paiement"
              defaultChecked={monthlySubscription}
              readonly={!!subscription}
              onchange={handleSubscriptionFrequencyChange}
            />
          )} */}
          {!subscription && (
            <Input
              name="subscriptionFrequency"
              uncheckedText="Paiment annuel"
              checkedText="Paiment mensuel"
              label="Paiement"
              defaultChecked={monthlySubscription}
              readonly={!!subscription}
              onchange={handleSubscriptionFrequencyChange}
            />
          )}
          <p>{getPaymentText(user, language)}</p>
          {!subscription && (
            <MainButton>
              {language == "En"
                ? "Activate my subscription"
                : "Activer mon abonnement"}
            </MainButton>
          )}
        </form>
      </FormProvider>
      <PaymentModal
        open={modalOpen}
        handleClose={() => {
          setModalOpen(false);
        }}
      />
    </>
  );
};

export default SubscriptionForm;
